import axios from 'axios';
import {jwtDecode} from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
import EventBus from './event-bus';

const API_URL = '/api/auth/login';
const REFRESH_URL = '/api/auth/refresh';
const LOGOUT_URL = '/api/auth/logout';
const REFRESH_INTERVAL = 1000;
const REFRESH_DELTA = 2000;
const LOGIN_AT_KEY = 'login_at';
const DAILY_LOGOUT_HOUR = 21;

class AuthService {
    user = null;
    refreshTimeout = null;
    installId = null;
    dailyLogoutDispatched = false;

    constructor() {
        this.installId = this.getOrCreateInstallId();

        this.enforceDailyLogout();

        if (this.token) {
            this.user = jwtDecode(this.token);
        }
        this.checkRefresh();
        this.listenForWakeUp();
    }

    set token(token) {
        if (token) {
            localStorage.setItem('token', token)
            this.user = jwtDecode(token);
        } else {
            localStorage.removeItem('token');
            this.user = null;
        }
    }

    get token() {
        return localStorage.getItem('token');
    }

    set refreshToken(refreshToken) {
        refreshToken
            ? localStorage.setItem('refresh_token', refreshToken)
            : localStorage.removeItem('refresh_token');
    }

    get refreshToken() {
        return localStorage.getItem('refresh_token');
    }

    getOrCreateInstallId() {
        let installId = localStorage.getItem('install_id');
        if (!installId) {
            installId = uuidv4();
            localStorage.setItem('install_id', installId);
        }
        return installId;
    }

    login(codice, password) {
        return axios
            .post(API_URL, {
                codice, password
            })
            .then(res => {
                localStorage.setItem(LOGIN_AT_KEY, String(Date.now()));
                this.token = res.data.token;
                this.refreshToken = res.data.refresh_token;
                this.setRefreshTimeout();

                EventBus.dispatch('LOGGED_IN');
                return res;
            })
            .catch(e => {
                console.warn(e)
                switch (e.response?.status) {
                    case 401:
                        throw new Error('Credenziali non valide');
                    case 409:
                        throw new Error('Sei già loggato da un altro dispositivo');
                    default:
                        throw new Error('Si è verificato un errore durante il login');
                }
            });
    }

    async refresh() {
        try {
            const res = await axios.post(REFRESH_URL, { refresh_token: this.refreshToken });
            this.token = res.data.token;
            this.refreshToken = res.data.refresh_token;
        } catch (e) {
            if (!e.response) return;
            if (e.response?.status === 401 && e.response?.data?.message?.includes('Session')) {
                console.warn('Sessione invalida - loggato da altro dispositivo');
                EventBus.dispatch('SESSION_INVALIDATED');
            }
            this.logout();
        }
    }

    async checkRefresh() {
        if (this.enforceDailyLogout(true)) {
            return;
        }

        if (this.user && (this.user.exp * 1000) < Date.now() + REFRESH_DELTA) {
            await this.refresh();
        }

        if (this.isAuthenticated()) {
            this.setRefreshTimeout();
        }
    }

    setRefreshTimeout() {
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
        }

        this.refreshTimeout = setTimeout(async () => await this.checkRefresh(), REFRESH_INTERVAL);
    }

    async logout() {
        if (this.isAuthenticated()) {
            try {
                await axios.post(LOGOUT_URL);
            } catch (e) {
                console.warn('Errore durante logout backend:', e);
                // Continua comunque con logout locale...
            }
        }

        return new Promise(resolve => {
            this.refreshToken = null;
            this.token = null;
            localStorage.removeItem(LOGIN_AT_KEY);
            if (this.refreshTimeout) {
                clearTimeout(this.refreshTimeout);
                this.refreshTimeout = null;
            }
            this.dailyLogoutDispatched = false;
            EventBus.dispatch('LOGGED_OUT');
            resolve(true);
        });
    }

    isAuthenticated() {
        return this.user && Date.now() < this.user?.exp * 1000;
    }

    enforceDailyLogout(notify = false) {
        if (!this.token || !this.shouldForceDailyLogout()) {
            return false;
        }

        if (notify) {
            if (!this.dailyLogoutDispatched) {
                this.dailyLogoutDispatched = true;
                EventBus.dispatch('DAILY_LOGOUT');
            }
        } else {
            this.clearLocalSession();
        }

        return true;
    }

    shouldForceDailyLogout() {
        const loginAt = this.getLoginAt();

        return loginAt !== null && loginAt < this.getLastDailyLogoutCutoff();
    }

    getLoginAt() {
        const storedLoginAt = Number(localStorage.getItem(LOGIN_AT_KEY));

        if (storedLoginAt > 0) {
            return storedLoginAt;
        }

        try {
            const issuedAt = jwtDecode(this.token)?.iat;
            return issuedAt ? issuedAt * 1000 : null;
        } catch {
            return null;
        }
    }

    getLastDailyLogoutCutoff() {
        const cutoff = new Date();
        cutoff.setHours(DAILY_LOGOUT_HOUR, 0, 0, 0);

        if (Date.now() < cutoff.getTime()) {
            cutoff.setDate(cutoff.getDate() - 1);
        }

        return cutoff.getTime();
    }

    clearLocalSession() {
        this.refreshToken = null;
        this.token = null;
        localStorage.removeItem(LOGIN_AT_KEY);

        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
            this.refreshTimeout = null;
        }
    }

    listenForWakeUp() {
        window.addEventListener('focus', () => this.checkRefresh());
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.checkRefresh();
            }
        });
    }
}

export default new AuthService();
